import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Card, Spinner, Alert, Button, Badge, Form, InputGroup, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileAlt, faEye, faBell, faSearch, faTimes, faFilter } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/setupAxios';
import Pagination from '../common/Pagination';
import useDebounce from '../../hooks/useDebounce';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardRow,
  MobileCardMeta,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;

const Devices = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: PER_PAGE, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const [subscribed, setSubscribed] = useState('');
  const [isCredit, setIsCredit] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchDevices = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { per_page: PER_PAGE, page: currentPage };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (subscribed !== '') params.subscribed = subscribed;
      if (isCredit !== '') params.is_credit = isCredit;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axiosInstance.get('/songs/devices', {
        params
      });
      const data = res.data;
      if (data?.success && Array.isArray(data?.data)) {
        setDevices(data.data);
        if (data.extra) {
          setPagination({
            current_page: data.extra.current_page ?? currentPage,
            last_page: data.extra.last_page ?? 1,
            per_page: data.extra.per_page ?? PER_PAGE,
            total: data.extra.total ?? 0
          });
        }
      } else {
        setDevices([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch devices');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, subscribed, isCredit, dateFrom, dateTo]);

  useEffect(() => {
    fetchDevices();
  }, [currentPage, debouncedSearch, subscribed, isCredit, dateFrom, dateTo]);

  const hasActiveFilters = subscribed !== '' || isCredit !== '' || dateFrom || dateTo;

  const clearFilters = () => {
    setSubscribed('');
    setIsCredit('');
    setDateFrom('');
    setDateTo('');
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewDetail = (deviceId) => {
    navigate(`/devices/detail?device_id=${encodeURIComponent(deviceId)}`);
  };

  const formatDate = (val) => {
    if (!val) return '—';
    try {
      const d = new Date(val);
      return isNaN(d.getTime()) ? val : d.toLocaleString();
    } catch (_) {
      return val;
    }
  };

  return (
    <div className="dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FontAwesomeIcon icon={faMobileAlt} className="me-2" />
          Devices
        </h2>
      </div>
      <p className="text-muted mb-3">
        Manage devices, balance (credits) and user subscriptions.
      </p>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Card className="mb-3">
        <Card.Body>
          <Form.Group className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by device ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
            </InputGroup>
          </Form.Group>
          <Row className="g-2 align-items-end">
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Subscribed
              </Form.Label>
              <Form.Select className="form-control form-select" value={subscribed} onChange={(e) => setSubscribed(e.target.value)}>
                <option value="">All</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Credit</Form.Label>
              <Form.Select className="form-control form-select" value={isCredit} onChange={(e) => setIsCredit(e.target.value)}>
                <option value="">All</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Registered from</Form.Label>
              <Form.Control size="sm" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="small mb-1">Registered to</Form.Label>
              <Form.Control size="sm" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Col>
            {hasActiveFilters && (
              <Col xs="auto">
                <Button size="sm" variant="outline-secondary" onClick={clearFilters}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Clear filters
                </Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="list-pagination-top">
            <Pagination
              currentPage={pagination.current_page}
              totalItems={pagination.total}
              itemsPerPage={pagination.per_page}
              onPageChange={handlePageChange}
              position="top"
            />
          </div>

          <ListDesktop>
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Device ID</th>
                  <th>Platform</th>
                  <th>Lang</th>
                  <th>Balance</th>
                  <th>Bonus</th>
                  <th>Subscription</th>
                  <th>PAYG</th>
                  <th>Subscribed</th>
                  <th>Premium expires</th>
                  <th>FCM / Notif</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="13" className="text-center py-4">
                      <Spinner animation="border" role="status" />
                      <span className="ms-2">Loading...</span>
                    </td>
                  </tr>
                ) : devices.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="text-center text-muted py-4">
                      No devices found.
                    </td>
                  </tr>
                ) : (
                  devices.map((d) => (
                    <tr key={d.id || d.device_id}>
                      <td>{d.id}</td>
                      <td>
                        <code className="small" style={{ wordBreak: 'break-all' }}>
                          {d.device_id || '—'}
                        </code>
                      </td>
                      <td>
                        <Badge bg="light" className="text-dark">{d.platform || '—'}</Badge>
                      </td>
                      <td>{d.device_lang || '—'}</td>
                      <td>
                        <strong>{d.balance ?? 0}</strong>
                      </td>
                      <td>{d.bonus_credits ?? 0}</td>
                      <td>{d.subscription_credits ?? 0}</td>
                      <td>{d.payg_credits ?? 0}</td>
                      <td>
                        {d.subscribed ? (
                          <Badge bg="success">Yes</Badge>
                        ) : (
                          <Badge bg="light" className="text-dark">No</Badge>
                        )}
                      </td>
                      <td>{formatDate(d.premium_expires_at)}</td>
                      <td>
                        {d.fcm_token ? (
                          <span className="text-success" title="FCM set">
                            <FontAwesomeIcon icon={faBell} />
                          </span>
                        ) : (
                          '—'
                        )}
                        {d.notification_permission && (
                          <Badge bg="light" className="ms-1 text-dark">Notif</Badge>
                        )}
                      </td>
                      <td>{formatDate(d.created_at)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewDetail(d.device_id)}
                          title="View detail"
                        >
                          <FontAwesomeIcon icon={faEye} className="me-1" />
                          Detail
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </ListDesktop>

          {!loading && devices.length > 0 && (
            <ListMobile>
              {devices.map((d) => (
                <MobileCard key={d.id || d.device_id} onClick={() => handleViewDetail(d.device_id)}>
                  <MobileCardTop>
                    <Badge bg="light" className="text-dark">{d.platform || '—'}</Badge>
                    {d.subscribed ? (
                      <Badge bg="success">Subscribed</Badge>
                    ) : (
                      <Badge bg="light" className="text-dark">Not subscribed</Badge>
                    )}
                  </MobileCardTop>
                  <MobileCardMeta>ID: {d.id}</MobileCardMeta>
                  <MobileCardRow label="Device ID" mono>
                    {d.device_id || '—'}
                  </MobileCardRow>
                  <MobileCardRow label="Balance">
                    <strong>{d.balance ?? 0}</strong>
                    {' · '}
                    Bonus {d.bonus_credits ?? 0}
                    {' · '}
                    Sub {d.subscription_credits ?? 0}
                  </MobileCardRow>
                  <MobileCardRow label="Lang">{d.device_lang || '—'}</MobileCardRow>
                  <MobileCardRow label="Premium expires">{formatDate(d.premium_expires_at)}</MobileCardRow>
                  <MobileCardRow label="Created">{formatDate(d.created_at)}</MobileCardRow>
                  <MobileCardFooter>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetail(d.device_id);
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} className="me-1" />
                      Detail
                    </Button>
                  </MobileCardFooter>
                </MobileCard>
              ))}
            </ListMobile>
          )}

          <Pagination
            currentPage={pagination.current_page}
            totalItems={pagination.total}
            itemsPerPage={pagination.per_page}
            onPageChange={handlePageChange}
            position="bottom"
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default Devices;
